import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../api/client';
import { Capability, CAPABILITY_NATURES, CAPABILITY_DOMAINS, DOMAIN_CAT } from '../types';
import { SidePanel } from '../components/common/SidePanel';
import { useT } from '../i18n';
import { Settings } from 'lucide-react';

export function InventoryCapabilitiesPage() {
  const { t } = useT();
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCapability, setSelectedCapability] = useState<Capability | null>(null);

  useEffect(() => {
    api.getCapabilities()
      .then((data: any) => setCapabilities(data))
      .finally(() => setLoading(false));
  }, []);

  const capabilityGrid = useMemo(() => {
    const grid: Record<string, Record<string, Capability[]>> = {};
    for (const domain of CAPABILITY_DOMAINS) {
      grid[domain] = {};
      for (const nature of CAPABILITY_NATURES) {
        grid[domain][nature] = [];
      }
    }
    for (const cap of capabilities) {
      if (grid[cap.domain]?.[cap.nature]) {
        grid[cap.domain][cap.nature].push(cap);
      }
    }
    return grid;
  }, [capabilities]);

  if (loading) {
    return (
      <div className="inventory-page">
        <div className="flex items-center justify-center" style={{ height: 200 }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (capabilities.length === 0) {
    return (
      <div className="inventory-page">
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Settings /></div>
            <div className="empty-state-title">{t('inv.capabilities.emptyTitle')}</div>
            <div className="empty-state-desc">{t('inv.capabilities.emptyDesc')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-page">
      <div className="matrix-grid-container">
        <div
          className="matrix-grid"
          style={{ '--matrix-cols': `48px repeat(${CAPABILITY_NATURES.length}, minmax(170px, 1fr))` } as React.CSSProperties}
        >
          <div className="matrix-grid-corner"></div>
          {CAPABILITY_NATURES.map((nature) => (
            <div key={nature} className="matrix-grid-col">{nature}</div>
          ))}

          {CAPABILITY_DOMAINS.map((domain) => (
            <React.Fragment key={domain}>
              <div className="matrix-grid-row">{domain}</div>
              {CAPABILITY_NATURES.map((nature) => (
                <div key={`${domain}-${nature}`} className="matrix-grid-cell matrix-grid-cell--wrap">
                  {capabilityGrid[domain]?.[nature]?.map((cap) => (
                    <div key={cap.id} className="inventory-card">
                      <span className="inventory-card-title">{cap.title}</span>
                      <button
                        className="inventory-card-info"
                        onClick={() => setSelectedCapability(cap)}
                        title={t('inv.showDetails')}
                      >
                        i
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      <SidePanel
        open={!!selectedCapability}
        onClose={() => setSelectedCapability(null)}
        title={selectedCapability?.title || ''}
      >
        {selectedCapability && (
          <>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.nature')}</div>
              <div className="panel-detail-value">{selectedCapability.nature}</div>
            </div>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.domainStakeholder')}</div>
              <div className="panel-detail-value">
                <span className={`meta-tag meta-tag--cat-${DOMAIN_CAT[selectedCapability.domain] || 1}`}>
                  {selectedCapability.domain}
                </span>
              </div>
            </div>
            <div className="panel-detail-section">
              <div className="panel-detail-label">{t('inv.label.description')}</div>
              <div className="panel-detail-value">
                {selectedCapability.description || t('inv.noDescription')}
              </div>
            </div>
          </>
        )}
      </SidePanel>
    </div>
  );
}
